import {
  createOrUpdateSubscription,
  fetchCustomerAllUsersEmail,
} from '@src/api/emailSubscriptions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { EmailSubscription } from '../types';
import { getESDefaultMetricCategories, isSearchedItem } from '../utils';
import { message } from 'antd';
import { useResetPanelAtoms } from '../hooks';
import { CURRENT_PRODUCT_MODE, CDN_ACCOUNT_TYPE } from '@src/utils/constants';
import { AI_ALERT_STANDARD_METRICS_MAP } from '@src/components/ai-alerts/constants';
import {
  isInternalUserSelector,
  aiBrokenAssetsSelector,
  accountTypeSelector,
} from '@src/store/common/selectors';
import {
  AUTO_INSIGHT_NOTIFICATION_CATEGORY,
  AUTO_INSIGHT_NOTIFICATION_ITEMS_MAP,
} from '@src/components/notification-channel/utils';

export const useFetchCustomerAllUsersEmail = (enabled) => {
  const isInternalUser = useSelector(isInternalUserSelector);
  return useQuery(
    ['all-users-email'],
    async () => {
      const response = await fetchCustomerAllUsersEmail(isInternalUser).then(
        (response) => response.data,
      );
      return response;
    },
    {
      refetchOnWindowFocus: false,
      enabled,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useGetAIAlertMetricsCategories = (
  aiAlertMetricsMeta,
  searchText: string = '',
  categoryMap: { key: string; metrics: any[] }[] = [],
  allESMetricKeys: string[] = [],
) => {
  const defaultCategories = getESDefaultMetricCategories()[CURRENT_PRODUCT_MODE];

  // Collect metrics by type
  const standardMetrics: any[] = [];
  const flowCategories: Record<string, any[]> = {};
  const customMetrics: any[] = [];

  aiAlertMetricsMeta.forEach((metric) => {
    const { name, isCustom, isFlow, flowCategory } = metric;
    if (!isCustom && !isFlow && !AI_ALERT_STANDARD_METRICS_MAP.AI.includes(name)) {
      standardMetrics.push(metric);
      allESMetricKeys.push(name);
    } else if (isCustom) {
      if (isFlow && flowCategory) {
        if (!flowCategories[flowCategory]) flowCategories[flowCategory] = [];
        flowCategories[flowCategory].push(metric);
        allESMetricKeys.push(name);
      } else {
        customMetrics.push(metric);
        allESMetricKeys.push(name);
      }
    }
  });

  // Build ordered array
  if (standardMetrics.length) {
    categoryMap.push({ key: defaultCategories.STANDARD_METRICS, metrics: standardMetrics });
  }
  if (customMetrics.length) {
    categoryMap.push({ key: defaultCategories.CUSTOM_METRICS, metrics: customMetrics });
  }
  Object.keys(flowCategories)
    .sort()
    .forEach((flowCat) => {
      categoryMap.push({ key: `Flow: ${flowCat}`, metrics: flowCategories[flowCat] });
    });

  // Filter metrics in each category by searchText
  const filteredCategoryMap = categoryMap
    .map((category) => ({
      ...category,
      metrics: category.metrics.filter(({ displayName }) =>
        isSearchedItem(displayName, searchText),
      ),
    }))
    .filter((category) => category.metrics.length > 0);

  return { categoryMap: filteredCategoryMap, allESMetricKeys };
};

export const useESMetricsCategories = (aiAlertMetricsMeta, searchText = '') => {
  const aiBrokenAssets = useSelector(aiBrokenAssetsSelector);
  const customerAccountType = useSelector(accountTypeSelector);

  const categoryMap: { key: string; metrics: any[] }[] = [];
  const allESMetricKeys: string[] = [];

  if (customerAccountType !== CDN_ACCOUNT_TYPE && aiBrokenAssets) {
    categoryMap.push({
      key: AUTO_INSIGHT_NOTIFICATION_CATEGORY,
      metrics: AUTO_INSIGHT_NOTIFICATION_ITEMS_MAP,
    });
  }

  const { categoryMap: updatedCategoryMap, allESMetricKeys: updatedAllESMetricKeys } =
    useGetAIAlertMetricsCategories(aiAlertMetricsMeta, searchText, categoryMap, allESMetricKeys);

  return { categoryMap: updatedCategoryMap, allESMetricKeys: updatedAllESMetricKeys };
};

export const useEditSubscription = () => {
  const queryClient = useQueryClient();
  const resetAtoms = useResetPanelAtoms();

  return useMutation(
    (payload: EmailSubscription) => {
      return createOrUpdateSubscription(payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['email-subscriptions-list']);
        resetAtoms.resetEditFormAtom();
      },
      onError: () => {
        message.error('There was an error editing subscription. Please try again later.');
      },
    },
  );
};

export const useCreateSubscription = () => {
  const queryClient = useQueryClient();
  const resetAtoms = useResetPanelAtoms();

  return useMutation(
    (payload: EmailSubscription) => {
      return createOrUpdateSubscription(payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['email-subscriptions-list']);
        resetAtoms.resetCreateFormAtom();
      },
      onError: () => {
        message.error('There was an error creating subscription. Please try again later.');
      },
    },
  );
};
